import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import React, { FC } from 'react';

import { AbgTypography, mergeSx, useTranslation } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useBookingWidgetContext } from '../../../organisms/BookingWidget/hooks';
import {
  DateTimePickerErrorEnum,
  DateTimePickerProperties,
  HandleCancel,
} from '../types';

import {
  buttonCancelStyles,
  buttonDoneStyles,
  doneButtonContainerStyles,
  pickerFooterContainerStyles,
} from './DateTimePickerFooter.styling';
type DateTimePickerFooterProps = Pick<
  DateTimePickerProperties,
  'brand' | 'uiTextData'
> & {
  isDropoffTimeUnreachable: boolean;
  daysSelected: number;
  showDateTimePickerError: DateTimePickerErrorEnum | boolean;
  onClose: HandleCancel;
  onDone: () => void;
};

export const DateTimePickerFooter: FC<DateTimePickerFooterProps> = ({
  brand,
  isDropoffTimeUnreachable,
  uiTextData,
  daysSelected,
  showDateTimePickerError,
  onClose,
  onDone,
}) => {
  const { model, partProps } = useBookingWidgetContext();
  const { features } = usePageContext();
  const isNewBookingWidget =
    features?.['feature.enableNewBookingWidget'] ?? false;
  const t = useTranslation(model.i18n);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { buttonCancelTitle, buttonDoneTitle } = uiTextData;

  const daysSelectedTitle = t('bookingWidget.date.selecteddays.label', {
    count: daysSelected,
  });

  return (
    <Box sx={pickerFooterContainerStyles}>
      <Button
        variant="contained"
        sx={mergeSx(
          buttonCancelStyles,
          partProps?.cancelButton?.styleParts?.root,
        )}
        onClick={onClose}
        data-testid="buttonCancel"
      >
        {buttonCancelTitle}
      </Button>
      <Box sx={doneButtonContainerStyles}>
        {daysSelected > 0 && (
          <AbgTypography
            variant="bodyRegularMedium"
            color="#65615D"
            data-testid="daysSelectedCaption"
          >
            {daysSelectedTitle}
          </AbgTypography>
        )}
        {(isMobile || !isNewBookingWidget) && (
          <Button
            variant="outlined"
            sx={buttonDoneStyles(brand)}
            onClick={onDone}
            disabled={
              daysSelected < 1 ||
              !!showDateTimePickerError ||
              isDropoffTimeUnreachable
            }
            data-testid="buttonDone"
          >
            {buttonDoneTitle}
          </Button>
        )}
      </Box>
    </Box>
  );
};
